import { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { Button } from "@mui/material";
import { useSnackbar } from "notistack";

import analytics from "fond/analytics";
import { selectCurrentAccount, selectUserId } from "fond/api";
import { STARRED_PROJECTS_KEY } from "fond/constants";
import { ResourceEntity } from "fond/types";
import { StarEventSource } from "fond/types/analytics";
import { getItem, setItem } from "fond/utils/localStorage";

type UseStarredReturn = {
  starredKey: string;
  starred: string[];
  addToStarred: (entity: ResourceEntity, source: StarEventSource) => void;
  removeFromStarred: (entity: ResourceEntity, source: StarEventSource) => void;
};

export const useStarred = (): UseStarredReturn => {
  const userId = useSelector(selectUserId);
  const accountId = useSelector(selectCurrentAccount)?.ID;
  const starredKey = `${STARRED_PROJECTS_KEY}_${userId}_${accountId}`;
  const { enqueueSnackbar } = useSnackbar();
  const [starred, setStarred] = useState<string[]>(getItem(starredKey, []));

  const addToStarred = useCallback(
    async (entity: ResourceEntity, source: StarEventSource) => {
      const starredItems: string[] = getItem(starredKey, []);

      // Attempt to add to starred only if it is not in starred
      if (!starredItems.includes(entity.ID)) {
        const newStarredItems = [...starredItems, entity.ID];
        setItem(starredKey, newStarredItems);
        // Forces components to rerender with storage event update
        const newStorageEvent = new StorageEvent("storage", {
          key: starredKey,
          oldValue: JSON.stringify(starredItems),
          newValue: JSON.stringify(newStarredItems),
        });
        window.dispatchEvent(newStorageEvent);

        const entityName = entity.EntityType === "project" ? entity.ProjectName : entity.Name;

        enqueueSnackbar(`"${entityName}" added to Starred`, {
          action: (
            <Button color="primary" onClick={() => removeFromStarred(entity, "undo")}>
              Undo
            </Button>
          ),
        });
      }

      analytics.track("entity.starred", {
        entityId: entity.ID,
        entityType: entity.EntityType,
        source,
      });
    },
    [starredKey, enqueueSnackbar]
  );

  const removeFromStarred = useCallback(
    async (entity: ResourceEntity, source: StarEventSource) => {
      const starredItems: string[] = getItem(starredKey, []);

      // Attempt to remove from starred only if it is in starred
      if (starredItems.includes(entity.ID)) {
        const entityName = entity.EntityType === "project" ? entity.ProjectName : entity.Name;
        const newStarredItems = starredItems.filter((item) => item !== entity.ID);
        setItem(starredKey, newStarredItems);
        const newStorageEvent = new StorageEvent("storage", {
          key: starredKey,
          oldValue: JSON.stringify(starredItems),
          newValue: JSON.stringify(newStarredItems),
        });
        window.dispatchEvent(newStorageEvent);

        enqueueSnackbar(`"${entityName}" removed from Starred`, {
          action: (
            <Button color="primary" onClick={() => addToStarred(entity, "undo")}>
              Undo
            </Button>
          ),
        });
      }

      analytics.track("entity.unstarred", {
        entityId: entity.ID,
        entityType: entity.EntityType,
        source,
      });
    },
    [starredKey, enqueueSnackbar]
  );

  useEffect(() => {
    const detectAccountItemChanges = (event: StorageEvent) => {
      if (event.key === starredKey) {
        setStarred(getItem(starredKey, []));
      }
    };
    window.addEventListener("storage", detectAccountItemChanges);
    return () => window.removeEventListener("storage", detectAccountItemChanges);
  }, [starredKey]);

  return { starredKey, starred, addToStarred, removeFromStarred };
};
